<template>
  <div class="page bg-background">
    <header v-if="showHeader" class="navbar fixed-top d-print-none">
      <div class="container-xl d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center" style="flex: 1; min-width: 0;">
          <a v-if="backButton"
             href="#"
             class="btn btn-icon me-2"
             @click.prevent="goBack">
            <IconArrowLeft class="icon" :size="24" />
          </a>
          <h1 class="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3 mb-0 text-truncate"
              style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0;">
            {{ pageTitle }}
          </h1>
        </div>
        <div class="d-flex align-items-center gap-2">
          <InboxBell />
          <button v-if="showMenu"
                  class="navbar-toggler position-relative"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#sidebar-menu"
                  aria-controls="navbar-menu"
                  aria-expanded="false"
                  aria-label="Toggle navigation">
            <span class="navbar-toggler-icon"></span>
            <span v-if="scanQueue.status.count > 0"
                  class="offline-badge position-absolute top-0 start-100 translate-middle badge rounded-pill text-white bg-danger">
              {{ scanQueue.status.count }}
            </span>
          </button>



        </div>
      </div>
    </header>
    <InboxBell v-if="showAltMenu && !showHeader" floating />
    <button v-if="showAltMenu"
            class="navbar-toggler text-bg-primary position-absolute alt-menu-btn"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#sidebar-menu"
            aria-controls="navbar-menu"
            aria-expanded="false"
            aria-label="Toggle navigation">
      <span class="navbar-toggler-icon"></span>
      <span v-if="scanQueue.status.count > 0"
            class="offline-badge position-absolute top-0 start-100 translate-middle badge rounded-pill text-white bg-danger">
              {{ scanQueue.status.count }}
            </span>
    </button>
    <div class="offcanvas offcanvas-end" tabindex="-1" id="sidebar-menu" aria-labelledby="sidebar-menu-label">
      <div class="offcanvas-header">
        <h5 class="offcanvas-title" id="sidebar-menu-label">{{ appName }}</h5>
        <button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
        <div v-if="isOutdated"
             class="alert alert-warning d-flex align-items-center mb-3 py-2"
             style="cursor: pointer;"
             @click="forceRefresh"
             data-bs-dismiss="offcanvas">
          <IconRefreshAlert class="me-2 flex-shrink-0" :size="18" />
          <small>{{ __DICT__.sidebar?.updateAvailable || 'Update available — tap to refresh' }}</small>
        </div>
        <ul class="list-group-flush list-group">
          <li class="list-group-item">
            <a class="nav-link" href="#" @click.prevent="changeWeek" data-bs-dismiss="offcanvas">
              <span class="nav-link-icon d-md-none d-lg-inline-block">
                <IconCalendar class="icon" :size="24" />
              </span>
              <span class="nav-link-title">{{ __DICT__.tray.changeWeek }}</span>
            </a>
          </li>
          <li class="list-group-item">
            <a class="nav-link" href="#" @click.prevent="lockApp" data-bs-dismiss="offcanvas">
              <span class="nav-link-icon d-md-none d-lg-inline-block">
                <IconLock class="icon" :size="24" />
              </span>
              <span class="nav-link-title">{{ __DICT__.tray.lock }}</span>
            </a>
          </li>
          <li class="list-group-item">
            <div class="nav-link d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <span class="nav-link-icon d-md-none d-lg-inline-block">
                  <IconMoon class="icon" :size="24" />
                </span>
                <span class="nav-link-title">{{ __DICT__.hub?.darkMode || 'Dark Mode' }}</span>
              </div>
              <div class="form-check form-switch mb-0">
                <input class="form-check-input" type="checkbox"
                       :checked="theme === 'dark'" @change="toggleTheme" />
              </div>
            </div>
          </li>
          <li v-if="scanQueue.status.count > 0" class="list-group-item">
            <a class="nav-link"
               href="#"
               @click.prevent="handleSync"
               :class="{'text-warning': !scanQueue.status.syncing, 'text-info': scanQueue.status.syncing}"
               :disabled="scanQueue.status.syncing || !isOnline"
               data-bs-dismiss="offcanvas">
              <span class="nav-link-icon d-md-none d-lg-inline-block">
                <div v-if="scanQueue.status.syncing" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">{{ __DICT__.sidebar?.syncing || 'Syncing...' }}</span>
                </div>
                <IconRefresh v-else class="icon" :size="24" />
              </span>
              <span class="nav-link-title">
                {{ scanQueue.status.syncing ? (__DICT__.sidebar?.syncing || 'Syncing...') : `${__DICT__.sidebar?.syncCount || 'Sync'} (${scanQueue.status.count})` }}
              </span>
            </a>
          </li>
          <li class="list-group-item">
            <a class="nav-link" href="#" @click.prevent="logOut" data-bs-dismiss="offcanvas">
              <span class="nav-link-icon d-md-none d-lg-inline-block">
                <IconLogout class="icon" :size="24" />
              </span>
              <span class="nav-link-title">{{ __DICT__.tray.logout }}</span>
            </a>
          </li>
          <li class="list-group-item">
            <a class="nav-link" href="#" @click.prevent="forceRefresh" data-bs-dismiss="offcanvas">
              <span class="nav-link-icon d-md-none d-lg-inline-block">
                <div v-if="isRefreshing" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Refreshing...</span>
                </div>
                <IconRefreshAlert v-else class="icon" :size="24" />
              </span>
              <span class="nav-link-title">{{ __DICT__.sidebar?.forceRefresh || 'Force Refresh' }}</span>
            </a>
          </li>
        </ul>
        <div class="text-muted mt-6 small">
          {{ __DICT__.sidebar?.version || 'Version:' }} {{ appVersion }}
          <br/>
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          {{weekStore.repObj?.repId}} - {{weekStore.repObj?.level}}
        </div>
      </div>
    </div>
    <div class="page-body bg-base">
      <div class="container-xl pt-1 mt-5">
        <AcknowledgementBanner />
        <div v-for="(notice, index) in scanQueue.status.notices" :key="index"
             class="alert alert-important alert-danger alert-dismissible" role="alert">
          <div>{{ queueNoticeText(notice) }}</div>
          <a class="btn-close" href="#" :aria-label="__DICT__.scan?.dismiss || 'Dismiss'"
             @click.prevent="scanQueue.dismissNotice(index)"></a>
        </div>
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import {api} from '@/services/api'
import {scanQueue} from '@/services/scanQueue.js'
import {ref, computed, watch, onMounted, onUnmounted, provide} from 'vue'
import {useAuthStore} from '@/stores/authStore'
import brandConfig from '@brand/config.js'
import {useRouter, useRoute} from "vue-router"
import {IconArrowLeft, IconCalendar, IconLock, IconRefresh, IconLogout, IconMoon, IconRefreshAlert} from '@tabler/icons-vue'
import {useTheme} from '@/composables/useTheme.js'
import {useVersionCheck} from '@/composables/useVersionCheck.js'
import InboxBell from '@/components/InboxBell.vue'
import AcknowledgementBanner from '@/components/AcknowledgementBanner.vue'

const {theme, toggleTheme} = useTheme()
const {isOutdated, checkNow, forceRefresh, isRefreshing} = useVersionCheck()

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
import {version} from '../../package.json'
import {useBottomNavStore} from '@/stores/bottomNavStore'
import {useWeekStore} from "@/stores/week.js";

const bottomNavStore = useBottomNavStore()
const weekStore = useWeekStore()



const SYNC_INTERVAL = 30000 // Check every 30 seconds
let syncInterval = null

const props = defineProps({
  pageTitle: {
    type: String,
    default: ''
  },
  showHeader: {
    type: Boolean,
    default: true
  },
  backButton: {
    type: Boolean,
    default: false
  },
  // Where the back button goes when there is no page to go back to (opened from a push)
  backTo: {
    type: [String, Object],
    default: null
  },
  showMenu: {
    type: Boolean,
    default: false
  },
  showAltMenu: {
    type: Boolean,
    default: false
  },
  showFooter: {
    type: Boolean,
    default: false
  }
})

const appVersion = ref(version)
const appName = ref(brandConfig.name)
const isOnline = ref(true)

// A queued scan the server refused, one it may have refused before, or one
// that could not be sent at all: the rep checks that guest by hand.
const queueNoticeText = (notice) => {
  if (notice.outcome === 'refused') return `${__DICT__.scan?.queuedScanRefused || 'Queued scan refused'}: ${notice.label} - ${notice.message}`
  if (notice.outcome === 'unknown') return `${__DICT__.scan?.queuedScanUnknown || 'Queued scan was sent before, outcome unknown, check by hand'}: ${notice.label}`
  return `${__DICT__.scan?.queuedScanGaveUp || 'Queued scan could not be sent, check by hand'}: ${notice.label}`
}

const attemptSync = async () => {
  if (!isOnline.value || scanQueue.status.count === 0 || scanQueue.status.syncing) {
    return
  }

  try {
    await scanQueue.replay()
  } catch (error) {
    console.error('Auto-sync failed:', error)
  }
}

const startSyncInterval = () => {
  if (syncInterval) {
    clearInterval(syncInterval)
  }
  syncInterval = setInterval(attemptSync, SYNC_INTERVAL)
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    attemptSync()
    checkNow()
  }
}

// Online status management
const onOnline = () => {
  isOnline.value = true
  attemptSync()
}

const onOffline = () => {
  isOnline.value = false
}

const handleSync = async () => {
  if (!isOnline.value) {
    return
  }

  try {
    await scanQueue.replay()
  } catch (error) {
    console.error('Sync failed:', error)
  }
}

onMounted(() => {
  // Set initial online status and add listeners
  if (typeof window !== 'undefined') {
    isOnline.value = navigator.onLine
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('focus', attemptSync)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Start periodic sync checks
    startSyncInterval()

    // Initial sync check
    attemptSync()

    // Check for version update after initial load
    setTimeout(() => checkNow(), 2000)
  }

  scanQueue.refreshCount()
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    window.removeEventListener('focus', attemptSync)
    document.removeEventListener('visibilitychange', handleVisibilityChange)

    if (syncInterval) {
      clearInterval(syncInterval)
    }
  }
})

const lockApp = async () => {
  authStore.setPwaUnlocked(false)
  localStorage.removeItem('lastActivity')
  await router.push({name: 'Unlock'})
}

const goBack = () => {
  if (props.backTo && !window.history.state?.back) router.replace(props.backTo)
  else router.back()
}

const changeWeek = async () => {
  localStorage.removeItem('weekId')
  await router.push("/")
}

const logOut = async () => {
  await api.logout()
  authStore.setPwaUnlocked(false)
  authStore.setLoggedIn(false)
  authStore.logOut()
  await router.push({name: 'Login'})
}

bottomNavStore.setBottomNavVisibility(props.showFooter)
watch(() => props.showFooter, (newValue) => {
  bottomNavStore.setBottomNavVisibility(newValue)
})

// Watch route changes to trigger sync
watch(() => route.path, () => {
  attemptSync()
})
</script>

<style scoped>
.alt-menu-btn {
  top: 10px;
  right: 10px;
  z-index: 999;
}

.offline-badge {
  font-size: 0.75rem;
}
</style>
